import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Warning, SignOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase A — Pilot Acceptance Gate
// Renders a full-screen blocking modal if the current user must accept the
// pilot agreement. Returns children when no acceptance is required (or after
// the user accepts in this session). Super admins bypass entirely.
export default function PilotAcceptanceGate({ user, onLogout, children }) {
  const [state, setState] = useState({ loading: true, must_accept: false, agreement: null, tenant: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user || user.role === "super_admin") {
      setState({ loading: false, must_accept: false });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/agreements/current`);
        setState({ loading: false, ...r.data });
      } catch (e) {
        // If 403 (pilot expired/suspended), the auth layer already shows the
        // error toast. Fall through to letting the app render normally; the
        // user will be forced to log out by the API layer on any other call.
        setState({ loading: false, must_accept: false });
      }
    })();
  }, [user]);

  const handleAccept = async () => {
    if (!state.agreement) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/agreements/accept`,
        { agreement_version: state.agreement.version });
      toast.success("Pilot agreement accepted");
      setState((s) => ({ ...s, must_accept: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return null;
  if (!state.must_accept || !state.agreement) return children;

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="bg-zinc-900 border-zinc-800 w-full max-w-3xl max-h-[90vh] flex flex-col"
        data-testid="pilot-acceptance-modal">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-3">
          <ShieldCheck size={20} weight="duotone" className="text-teal-400" />
          <div>
            <h2 className="text-base font-semibold text-zinc-100">{state.agreement.title}</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Version {state.agreement.version} · Effective {state.agreement.effective_date?.slice(0, 10)}
            </p>
          </div>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-sm p-3 mb-4 text-[11px] text-amber-200 flex items-start gap-2">
            <Warning size={13} weight="duotone" className="text-amber-300 mt-0.5 shrink-0" />
            <span>
              Acceptance of this Pilot Evaluation Agreement is required before you can use the platform.
              Your acceptance is recorded with timestamp, IP, and browser metadata for audit purposes.
            </span>
          </div>
          <pre className="text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap font-sans"
            data-testid="pilot-agreement-body">{state.agreement.content_md}</pre>
        </div>
        <div className="px-6 py-3 border-t border-zinc-800 flex items-center justify-between gap-2">
          <button onClick={onLogout}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
            data-testid="pilot-decline-logout">
            <SignOut size={12} /> Decline &amp; log out
          </button>
          <Button onClick={handleAccept} disabled={submitting}
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-semibold"
            data-testid="pilot-accept-btn">
            {submitting ? "Recording…" : "I accept the agreement"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function PilotExpirationBanner({ user }) {
  const [tenant, setTenant] = useState(null);
  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    (async () => {
      try {
        const r = await axios.get(`${API}/agreements/current`);
        if (r.data?.tenant?.tenant_type === "pilot") setTenant(r.data.tenant);
      } catch (e) { void e; }
    })();
  }, [user]);
  if (!tenant?.pilot_expires_at) return null;
  const ms = new Date(tenant.pilot_expires_at).getTime() - Date.now();
  const isExpired = ms <= 0;
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  const mode = (tenant.pilot_expiry_mode || "hard_block");

  // Already-expired pilot in read_only mode → loud red read-only banner
  if (isExpired && mode === "read_only") {
    return (
      <div className="px-3 py-1.5 text-[11px] font-mono border-b bg-red-500/20 border-red-500/40 text-red-100 flex items-center justify-center gap-1.5"
        data-testid="pilot-readonly-banner">
        <Warning size={11} weight="duotone" />
        Pilot evaluation expired on {tenant.pilot_expires_at.slice(0, 10)}.
        Platform is in <b>read-only mode</b> — viewing is allowed, all
        create/update/delete actions are blocked. Contact HoloTrak to
        reactivate or convert to production.
      </div>
    );
  }

  const tone =
    days <= 3  ? "bg-red-500/15 border-red-500/30 text-red-200" :
    days <= 14 ? "bg-amber-500/15 border-amber-500/30 text-amber-200" :
                 "bg-teal-500/10 border-teal-500/30 text-teal-200";
  const msg = days === 0
    ? `Pilot expires today (${tenant.pilot_expires_at.slice(0, 10)}). Contact HoloTrak to extend or convert.`
    : `Pilot expires in ${days} day${days === 1 ? "" : "s"} (${tenant.pilot_expires_at.slice(0, 10)}).`;
  return (
    <div className={`px-3 py-1.5 text-[11px] font-mono border-b ${tone} flex items-center justify-center gap-1.5`}
      data-testid="pilot-expiration-banner">
      <Warning size={11} weight="duotone" /> {msg}
    </div>
  );
}
